import React from 'react';

const features = [
  {
    tag: 'Analytics',
    tagColor: '#4f46e5',
    tagBg: 'rgba(99,102,241,0.1)',
    title: 'Real-Time Decision Dashboard',
    description:
      'Get a live, unified view of every metric that drives your decisions. Track KPIs, monitor trends, and receive intelligent alerts the moment something shifts — all in one place.',
    bullets: [
      'Customizable dashboard widgets',
      'Live data refresh every 30 seconds',
      'Smart anomaly detection & alerts',
      'Multi-source data aggregation',
    ],
    reverse: false,
  },
  {
    tag: 'AI',
    tagColor: '#7c3aed',
    tagBg: 'rgba(139,92,246,0.1)',
    title: 'AI-Powered Recommendations',
    description:
      'Stop guessing. Orvexis analyzes your historical data and current signals to surface the highest-impact actions — ranked by expected outcome and confidence level.',
    bullets: [
      'Context-aware AI suggestions',
      'Ranked by ROI & confidence score',
      'Learns from your team\'s past decisions',
      'Explainable AI — see why each action is recommended',
    ],
    reverse: true,
  },
  {
    tag: 'Planning',
    tagColor: '#db2777',
    tagBg: 'rgba(219,39,119,0.1)',
    title: 'Scenario Planning & Simulations',
    description:
      'Model the future before committing. Build what-if scenarios, adjust variables, and run AI simulations to see how outcomes change — without any risk to your live operations.',
    bullets: [
      'Drag-and-drop scenario builder',
      'Multi-variable sensitivity analysis',
      'Side-by-side scenario comparison',
      'Export scenarios as shareable reports',
    ],
    reverse: false,
  },
  {
    tag: 'Collaboration',
    tagColor: '#0369a1',
    tagBg: 'rgba(3,105,161,0.1)',
    title: 'Team Collaboration & Decision Logs',
    description:
      'Decisions made together stick. Invite your team, assign ownership, comment on insights, and maintain a full audit trail so every decision is traceable and accountable.',
    bullets: [
      'Role-based access control',
      'Threaded comments on any insight',
      'Decision history & version control',
      'Slack & email notifications',
    ],
    reverse: true,
  },
];

const integrations = [
  { name: 'Google Sheets', icon: '📊' },
  { name: 'Notion', icon: '📝' },
  { name: 'Salesforce', icon: '☁️' },
  { name: 'Slack', icon: '💬' },
  { name: 'PostgreSQL', icon: '🗄️' },
  { name: 'REST API', icon: '🔌' },
  { name: 'Excel', icon: '📈' },
  { name: 'Zapier', icon: '⚡' },
];

function FeatureRow({
  tag, tagColor, tagBg, title, description, bullets, reverse,
}: (typeof features)[0]) {
  return (
    <div className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} gap-12 items-center py-16 border-b border-gray-100 last:border-0`}>
      {/* Text */}
      <div className="flex-1">
        <span
          className="inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4"
          style={{ background: tagBg, color: tagColor }}
        >
          {tag}
        </span>
        <h2
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          {title}
        </h2>
        <p className="text-gray-500 leading-relaxed mb-6">{description}</p>
        <ul className="flex flex-col gap-3">
          {bullets.map(b => (
            <li key={b} className="flex items-start gap-3 text-sm text-gray-600">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: tagColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {b}
            </li>
          ))}
        </ul>
      </div>

      {/* Visual block */}
      <div
        className="flex-1 w-full rounded-2xl min-h-[260px] flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${tagBg}, rgba(248,250,252,0.6))`, border: `1px solid ${tagBg}` }}
      >
        <div className="text-center px-8 py-12">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm"
            style={{ background: tagColor }}
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm font-semibold" style={{ color: tagColor }}>{title}</p>
        </div>
      </div>
    </div>
  );
}

export default function Product() {
  return (
    <div className="bg-[#f8fafc] pt-24">
      {/* Hero */}
      <div className="max-w-screen-xl mx-auto px-6 text-center py-16 border-b border-gray-100">
        <span
          className="inline-block text-xs font-semibold uppercase tracking-widest px-4 py-1 rounded-full mb-6"
          style={{ background: 'rgba(99,102,241,0.1)', color: '#4f46e5' }}
        >
          The Platform
        </span>
        <h1
          className="text-4xl md:text-5xl font-bold text-gray-900 mb-5"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Every tool you need to decide with clarity
        </h1>
        <p className="text-lg text-gray-500 max-w-2xl mx-auto">
          Orvexis brings together real-time analytics, AI recommendations, and team collaboration into a single platform — so your best decisions happen faster.
        </p>
      </div>

      {/* Feature rows */}
      <div className="max-w-screen-xl mx-auto px-6">
        {features.map(f => (
          <FeatureRow key={f.title} {...f} />
        ))}
      </div>

      {/* Integrations */}
      <div className="bg-white border-t border-gray-100 py-16">
        <div className="max-w-screen-xl mx-auto px-6 text-center">
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Works with your existing stack
          </h2>
          <p className="text-gray-500 mb-10">
            Connect the tools your team already uses — no engineering required.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {integrations.map(({ name, icon }) => (
              <div
                key={name}
                className="flex flex-col items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-4 py-5 hover:shadow-sm hover:border-indigo-200 transition-all"
              >
                <span className="text-2xl">{icon}</span>
                <span className="text-sm font-medium text-gray-700">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 text-center px-6">
        <h2
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Ready to see it in action?
        </h2>
        <p className="text-gray-500 mb-8">Start free — no credit card required.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#"
            className="inline-flex items-center justify-center text-sm font-semibold text-white px-7 py-3.5 rounded-full shadow-md hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#6366f1' }}
          >
            Start for Free
          </a>
          <a
            href="/contact"
            className="inline-flex items-center justify-center text-sm font-semibold text-gray-700 px-7 py-3.5 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
          >
            Request a Demo
          </a>
        </div>
      </div>
    </div>
  );
}
